import type { PrintJobStatusView } from "@fiket/booth-schema";

/** 키오스크 폴링 간격 (스펙 §6: 2초) */
export const PRINT_POLL_INTERVAL_MS = 2_000;
/** 요청 후 이 시간 안에 claimed 가 안 되면 지연 안내 (스펙 §6: 20초) */
export const PRINT_CLAIM_DELAY_MS = 20_000;
/** printing 진입 뒤 링을 50%→95% 로 보간하는 기준 — SELPHY CP1500 평균 소요 (스펙 §6: 약 50초) */
export const PRINT_PRINTING_ETA_MS = 50_000;

export type PrintPhase = "preparing" | "printing" | "delayed" | "failed" | "paperOut" | "printed";

export interface PrintPhaseInput {
  /** 마지막으로 받은 상태. 첫 응답 전엔 null */
  status: PrintJobStatusView | null;
  nowMs: number;
  /** 인쇄 요청이 성공한 시각(클라이언트 시계). 서버 createdAt 은 iPad 와 시계가 다를 수 있어 쓰지 않는다 */
  startedAtMs: number;
}

/**
 * 상태·시간 → 화면 단계.
 * - 20초 안에 claimed 가 안 되거나 에이전트가 오프라인이면 지연 안내(폴링은 계속 — 늦게 printed 가 오면 마침으로)
 * - failed 는 paper_out 만 따로 — 운영자가 원인을 바로 알게
 * - cancelled(콘솔 취소)는 손님에겐 실패와 같다
 */
export function derivePrintPhase({ status, nowMs, startedAtMs }: PrintPhaseInput): PrintPhase {
  const claimOverdue = nowMs - startedAtMs >= PRINT_CLAIM_DELAY_MS;
  if (!status) return claimOverdue ? "delayed" : "preparing";
  switch (status.status) {
    case "printed":
      return "printed";
    case "failed":
      return status.errorCode === "paper_out" ? "paperOut" : "failed";
    case "cancelled":
      return "failed";
    case "queued":
      return !status.agentOnline || claimOverdue ? "delayed" : "preparing";
    case "claimed":
    case "printing":
      return status.agentOnline ? "printing" : "delayed";
  }
}

/**
 * 링 진행률 0..1. undefined = 무한 회전(RingSpinner 규약).
 * claimed 25% → printing 진입 50% → ETA 동안 95% 까지 보간(완료는 서버 printed 로만) → printed 100%.
 * printingSinceMs 는 클라이언트가 printing 을 처음 관측한 시각(서버 printingAt 은 시계 차이로 쓰지 않음).
 */
export function printProgress(
  status: PrintJobStatusView | null,
  nowMs: number,
  printingSinceMs: number | null
): number | undefined {
  if (!status || status.status === "queued") return undefined;
  switch (status.status) {
    case "claimed":
      return 0.25;
    case "printing": {
      const since = printingSinceMs ?? nowMs;
      const f = Math.min(1, Math.max(0, (nowMs - since) / PRINT_PRINTING_ETA_MS));
      return 0.5 + 0.45 * f;
    }
    case "printed":
      return 1;
    case "failed":
    case "cancelled":
      return 0;
  }
}

/** printed·cancelled 에서만 폴링 종료. failed 는 콘솔 "재시도" 로 queued 로 되살 수 있어 계속 본다 */
export function isPollTerminal(status: PrintJobStatusView): boolean {
  return status.status === "printed" || status.status === "cancelled";
}
